import * as React from 'react'
import { useEffect, useState } from 'react'

import { CalendarIcon, ListIcon, MapIcon } from 'lucide-react'
import {
  action,
  IReactionDisposer,
  makeObservable,
  observable,
  reaction,
} from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import DayBar from '~/client/src/shared/components/DayBar'
import Dimmer from '~/client/src/shared/components/Dimmer'
import {
  Content,
  Footer,
  Header,
  View,
} from '~/client/src/shared/components/Layout'
import { Loader } from '~/client/src/shared/components/Loader'
import FormTypeSelectionForm from '~/client/src/shared/components/SitePermitCreationForm/FormTypeSelectionForm'
import SitePermitCreationForm from '~/client/src/shared/components/SitePermitCreationForm/WorkflowCreationForm'
import WorkflowViewFormWrapper from '~/client/src/shared/components/SitePermitCreationForm/WorkflowViewFormWrapper'
import ViewMode from '~/client/src/shared/components/ViewSelector/ViewMode'
import ViewSelector from '~/client/src/shared/components/ViewSelector/ViewSelector'
import { UrlParamKey } from '~/client/src/shared/constants/commonRoutes'
import CalendarEventLabelType from '~/client/src/shared/enums/CalendarEventLabelType'
import useWorkflows from '~/client/src/shared/hooks/useWorkflows'
import useWorkflowsByIds from '~/client/src/shared/hooks/useWorkflowsByIds'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import CalendarEvent, {
  CalendarEventEntityType,
} from '~/client/src/shared/models/CalendarEvent'
import SitePermit from '~/client/src/shared/models/Permit'
import ClosuresStore from '~/client/src/shared/stores/domain/Closures.store'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import FormCategoriesStore from '~/client/src/shared/stores/domain/FormCategories.store'
import GlobeViewsStore from '~/client/src/shared/stores/domain/GlobeViews.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import MarkupsStore from '~/client/src/shared/stores/domain/Markups.store'
import PermitTypesStore from '~/client/src/shared/stores/domain/PermitTypes.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import ProjectsStore from '~/client/src/shared/stores/domain/Projects.store'
import SharedSitePermitStore from '~/client/src/shared/stores/domain/SharedSitePermit.store'
import SitemapsStore from '~/client/src/shared/stores/domain/Sitemaps.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import TilesetsStore from '~/client/src/shared/stores/domain/Tilesets.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import WorkflowDrawingGuardStore from '~/client/src/shared/stores/ui/WorkflowDrawingGuard.store'
import { NOOP } from '~/client/src/shared/utils/noop'
import { resolveFormTypeDeepLink } from '~/client/src/shared/utils/resolveFormTypeDeepLink'
import { toast } from '~/client/src/shared/utils/toast'
import {
  getSearchParam,
  removeFromSearchParam,
  urlHasSearchParam,
} from '~/client/src/shared/utils/urlutils'

import BottomNavigationNavBar from '../../components/BottomNavigationNavBar/BottomNavigationNavBar'
import MobileFileInput from '../../components/FileInput/MobileFileInput'
import MobileEventStore from '../../stores/EventStore/MobileEvents.store'
import MobileInitialState from '../../stores/MobileInitialState'
import MobileCommonStore from '../../stores/ui/MobileCommon.store'
import MobileLogisticsCalendar from './components/LogisticsCalendar/LogisticsCalendar'
import MobileLogisticsList from './components/LogisticsList/LogisticsList'
import MobileLogisticsMap from './components/LogisticsMap'
import MobileLogisticsStore, {
  LogisticsPageViewMode,
} from './stores/MobileLogistics.store'
import MobileLogisticsFilterStore from './stores/MobileLogisticsFilter.store'
import MobileLogisticsListStore from './stores/MobileLogisticsList.store'
import MobileLogisticsMapStore from './stores/MobileLogisticsMap.store'

import './Logistics.scss'

const URL_ACTION_PARAM = 'action'
const ARROW_HEIGHT = 15

interface IProps {
  state?: MobileInitialState
  eventsStore?: MobileEventStore
  sitemapsStore?: SitemapsStore
  common?: MobileCommonStore
  projectDateStore?: ProjectDateStore
  permitTypesStore?: PermitTypesStore
  closuresStore?: ClosuresStore
  locationAttributesStore?: LocationAttributesStore
  companiesStore?: CompaniesStore
  projectMembersStore?: ProjectMembersStore
  tagsStore?: TagsStore
  globeViewsStore?: GlobeViewsStore
  formCategoriesStore?: FormCategoriesStore
  userProjectsStore?: UserProjectsStore
  sharedSitePermitStore?: SharedSitePermitStore
  projectsStore?: ProjectsStore
  tilesetsStore?: TilesetsStore
  markupsStore?: MarkupsStore
  workflowDrawingGuardStore?: WorkflowDrawingGuardStore
  workflows?: SitePermit[]
  workflowsLoading?: boolean
  onDatesFilterChange?: (filter: {
    startDate?: number
    endDate?: number
  }) => void
  deepLinkedForm?: SitePermit
  deepLinkFormType?: string
}

@inject(
  'state',
  'common',
  'eventsStore',
  'sitemapsStore',
  'projectDateStore',
  'permitTypesStore',
  'closuresStore',
  'locationAttributesStore',
  'companiesStore',
  'projectMembersStore',
  'tagsStore',
  'globeViewsStore',
  'formCategoriesStore',
  'userProjectsStore',
  'sharedSitePermitStore',
  'projectsStore',
  'tilesetsStore',
  'markupsStore',
  'workflowDrawingGuardStore',
)
@observer
class LogisticsComponent extends React.Component<IProps> {
  private readonly store: MobileLogisticsStore
  private readonly logisticListStore: MobileLogisticsListStore
  private readonly logisticsMapStore: MobileLogisticsMapStore
  private readonly logisticsFilterStore: MobileLogisticsFilterStore = null
  private viewingDateDisposer: IReactionDisposer
  private visibleWorkflowsDisposer: IReactionDisposer
  private formTypeReactionDisposer: IReactionDisposer
  @observable private isDatePickerShown: boolean = false

  @observable private isFormTypeSelectionFormShown: boolean = false
  @observable private isPermitCreationFormShown: boolean = false

  public constructor(props: IProps) {
    super(props)

    this.store = new MobileLogisticsStore(
      props.projectDateStore,
      props.eventsStore,
      props.sitemapsStore,
      props.globeViewsStore,
      props.tilesetsStore,
      props.closuresStore,
      props.permitTypesStore,
      props.formCategoriesStore,
      props.locationAttributesStore,
      props.companiesStore,
      props.sharedSitePermitStore,
    )

    this.logisticListStore = new MobileLogisticsListStore(
      this.store,
      props.projectDateStore,
      props.projectMembersStore,
      props.companiesStore,
      props.common,
      props.state,
      props.sharedSitePermitStore,
      this.store.openWorkflowViewForm,
    )

    this.logisticsMapStore = new MobileLogisticsMapStore(
      this.store,
      props.companiesStore,
    )

    this.logisticsFilterStore = new MobileLogisticsFilterStore(
      props.eventsStore,
      this.store,
      this.logisticListStore,
      NOOP,
      props.locationAttributesStore,
      props.permitTypesStore,
      props.tagsStore,
      props.formCategoriesStore,
      props.userProjectsStore,
      props.projectsStore,
      props.sharedSitePermitStore,
    )

    this.setViewModeFromHash()
    this.store.initPostEventCallback()
    makeObservable(this)
  }

  private setViewModeFromHash = () => {
    const hash = window.location.hash.replace('#', '')
    if (
      Object.values(LogisticsPageViewMode).includes(
        hash as LogisticsPageViewMode,
      )
    ) {
      this.store.setViewMode(hash as LogisticsPageViewMode)
    }
  }

  public componentDidMount(): void {
    const { common, state, projectDateStore, onDatesFilterChange } = this.props

    common.hideNavBar()

    this.tryOpenFormTypeFromDeepLink()

    // When permit types haven't loaded yet, retry the deep link once they arrive.
    if (
      this.props.deepLinkFormType &&
      !this.props.permitTypesStore.isDataReceived
    ) {
      this.formTypeReactionDisposer = reaction(
        () => this.props.permitTypesStore.isDataReceived,
        isReady => {
          if (isReady) {
            this.tryOpenFormTypeFromDeepLink()
            this.formTypeReactionDisposer?.()
          }
        },
      )
    }

    this.runActionFromURL()

    if (state.preSelectedPermitTypeId && !this.handledFormType) {
      this.openFormTypeSelectionForm()
    }

    common.addNavigationListener(this.runActionFromURL)

    // Open the deep-linked form if it resolved before this view mounted;
    // componentDidUpdate only catches it when it arrives afterwards.
    if (this.props.deepLinkedForm) {
      this.store.openWorkflowViewForm(this.props.deepLinkedForm)
    }

    this.viewingDateDisposer = reaction(
      () => this.store.viewingDate,
      date => {
        onDatesFilterChange?.({
          startDate: projectDateStore.startOfDay(date).getTime(),
          endDate: projectDateStore.endOfDay(date).getTime(),
        })
      },
    )

    // Bridge the currently-visible (filtered) workflows to MarkupsStore so the
    // Precise Location modal scopes other workflows' markups to the same set —
    // mirrors LogisticsMapView, but here it survives the workflow form replacing
    // the map view (which would otherwise leave the bridge stale → shows all).
    this.visibleWorkflowsDisposer = reaction(
      () => this.logisticsMapStore.filteredPermits,
      permits => {
        const markupsStore = this.props.markupsStore
        const ids = permits.map(p => p.id)
        markupsStore?.setVisibleWorkflows(
          new Map(permits.map(permit => [permit.id, permit])),
        )
        // Load the visible workflows' markups; evict the ones that left the view.
        markupsStore?.ensureLoadedForSitePermits(ids)
        markupsStore?.retainSitePermits(ids)
      },
      { fireImmediately: true },
    )
  }

  public componentWillUnmount(): void {
    this.store.clearPostEventCallback()
    this.props.common.removeNavigationListener(this.runActionFromURL)
    this.viewingDateDisposer?.()
    this.visibleWorkflowsDisposer?.()
    this.formTypeReactionDisposer?.()
    this.props.markupsStore?.setVisibleWorkflows(null)
  }

  public componentDidUpdate(prevProps: IProps) {
    this.store.ensureCorrectViewMode()
    this.runActionFromURL()

    if (prevProps.workflows !== this.props.workflows) {
      this.store.setAllForms(this.props.workflows || [])
    }

    if (prevProps.workflowsLoading !== this.props.workflowsLoading) {
      this.store.setWorkflowsLoading(this.props.workflowsLoading ?? true)
    }

    // Open when the deep-linked form changes to a present one (covers async
    // resolve and re-deep-linking the same form after it was closed).
    if (
      this.props.deepLinkedForm &&
      prevProps.deepLinkedForm?.id !== this.props.deepLinkedForm.id
    ) {
      this.store.openWorkflowViewForm(this.props.deepLinkedForm)
    }

    this.tryOpenFormTypeFromDeepLink()
  }

  @action.bound
  private runActionFromURL() {
    if (urlHasSearchParam(URL_ACTION_PARAM)) {
      const action = getSearchParam(URL_ACTION_PARAM)

      switch (action) {
        case 'createForm':
          if (!this.handledFormType) {
            this.openFormTypeSelectionForm()
          }
      }

      removeFromSearchParam(URL_ACTION_PARAM, this.props.common.history)
    }
  }

  public render(): JSX.Element {
    if (this.isFormTypeSelectionFormShown) {
      return (
        <FormTypeSelectionForm
          onSelect={this.openSpecificPermitCreationForm}
          onClose={this.dismissFormTypeSelectionForm}
          titleVariant="panel"
        />
      )
    }
    if (this.isPermitCreationFormShown) {
      return (
        <>
          <SitePermitCreationForm
            close={this.closePermitCreationForm}
            FileInputType={MobileFileInput}
            calendarEventDates={this.store.calendarEventDates}
            openWorkflow={this.store.previewLinkedWorkflow}
          />
          {this.renderLinkedWorkflowPreview()}
        </>
      )
    }

    if (this.store.isWorkflowViewFormShown && this.store.displayedWorkflow) {
      return (
        <>
          <WorkflowViewFormWrapper
            key={this.store.displayedWorkflow.id}
            workflowToShow={this.store.displayedWorkflow}
            onClose={this.closePermitViewForm}
            FileInputType={MobileFileInput}
            isMobileMode={true}
            openWorkflow={this.store.previewLinkedWorkflow}
          />
          {this.renderLinkedWorkflowPreview()}
        </>
      )
    }

    const shouldBeScrollable =
      (this.store.selectedViewMode === LogisticsPageViewMode.List ||
        this.store.selectedViewMode === LogisticsPageViewMode.Calendar) &&
      !this.isDatePickerShown

    return (
      <View
        className={classList({
          logistics: true,
          scrollable: shouldBeScrollable,
          'overflow-hidden': !shouldBeScrollable,
        })}
      >
        <div className="col relative">
          <Header>{this.renderHeader()}</Header>
          <Content scrollable={false}>{this.content}</Content>
        </div>
        <Footer className="sticky logistics-footer">
          <BottomNavigationNavBar onCreateClicked={this.toggleCreationPopup} />
        </Footer>
      </View>
    )
  }

  private renderHeader(): JSX.Element {
    const { shouldShowHeader } = this.props.common
    if (!shouldShowHeader) {
      return null
    }

    const isTransparentHeader =
      this.store.selectedViewMode === LogisticsPageViewMode.Map &&
      !this.isDatePickerShown

    return (
      <div
        className={classList({
          'row px20 py5 logistics-header': true,
          'logistics-transparent-header': isTransparentHeader,
        })}
      >
        <DayBar
          className="bg-transparent"
          isArrowsHidden={true}
          shouldIconRender={false}
          arrowsHeight={ARROW_HEIGHT}
          currentStartDate={this.store.viewingDate}
          setDate={this.store.setViewingDate}
          isOneDayMode={true}
          setDatePickerState={this.setDatePickerState}
          shouldRenderChevron={true}
        />
        <ViewSelector
          viewModes={this.viewModes}
          selectedViewMode={this.store.selectedViewMode}
          selectViewMode={this.selectViewModeGuarded}
        />
      </div>
    )
  }

  private get content(): JSX.Element {
    const { selectedViewMode } = this.store

    if (this.store.isLoaderShown) {
      return <Loader hint={Localization.translator.loadingFormsData} />
    }

    return (
      <>
        <div className="full-height relative">
          {selectedViewMode === LogisticsPageViewMode.Map && (
            <MobileLogisticsMap
              openPermitViewForm={this.store.openWorkflowViewForm}
              openWorkflowCreationForm={this.openSpecificPermitCreationForm}
              openFormTypeSelectionForm={this.openFormTypeSelectionForm}
              logisticsStore={this.store}
              logisticsMapStore={this.logisticsMapStore}
              logisticsFilterStore={this.logisticsFilterStore}
            />
          )}
          {selectedViewMode === LogisticsPageViewMode.List && (
            <MobileLogisticsList
              store={this.store}
              logisticsListStore={this.logisticListStore}
              logisticsFilterStore={this.logisticsFilterStore}
            />
          )}
          {selectedViewMode === LogisticsPageViewMode.Calendar && (
            <MobileLogisticsCalendar
              logisticsStore={this.store}
              onNewEventCreate={this.onNewEventCreate}
              onEventClicked={this.onEventClicked.bind(this)}
              logisticsFilterStore={this.logisticsFilterStore}
              logisticsListStore={this.logisticListStore}
            />
          )}
        </div>
      </>
    )
  }

  @action.bound
  public onNewEventCreate(startDate?: Date, endDate?: Date) {
    if (startDate && endDate) {
      this.setPlaceholderDates(startDate, endDate)
      this.openFormTypeSelectionForm()
    }
  }

  public onEventClicked(event: CalendarEvent) {
    const isNewType = event.labelType === CalendarEventLabelType.New

    if (isNewType || event.isShared) {
      this.props.common.displaySharedFormView(event.data.id)
      return
    }

    switch (event.entityType) {
      case CalendarEventEntityType.Form:
        this.store.openWorkflowViewForm(event.data)
        break
    }
  }

  private setPlaceholderDates = (startDate: Date, endDate: Date): void => {
    this.store.setPlaceholderStartDate(startDate)
    this.store.setPlaceholderEndDate(endDate)
  }

  @action
  private toggleCreationPopup = (): void => {
    this.store.resetPlaceholderDates()
    this.openFormTypeSelectionForm()
  }

  @action.bound
  private openPermitCreationForm() {
    this.isPermitCreationFormShown = true
  }

  @action.bound
  private openFormTypeSelectionForm() {
    // "+ Create" starts the alternative creation path — confirm first when a
    // map-first drawing session would lose markups (no-op otherwise).
    this.props.workflowDrawingGuardStore.guard(
      action(() => {
        this.isFormTypeSelectionFormShown = true
      }),
    )
  }

  // Leaving Map mode unmounts the map; setViewMode also writes
  // window.location.hash directly, which the history guard can't intercept —
  // pre-empt it here.
  private selectViewModeGuarded = (mode: LogisticsPageViewMode): void => {
    // ViewSelector fires on the active tab too, and setViewMode is idempotent for
    // the same mode — guarding that tap would offer to discard a draft for nothing.
    if (mode === this.store.selectedViewMode) {
      return
    }
    this.props.workflowDrawingGuardStore.guard(() =>
      this.store.setViewMode(mode),
    )
  }

  // Both closes drop a location picked from the map, or the next "+ Create" would inherit it.
  @action
  private closePermitCreationForm = (): void => {
    this.isPermitCreationFormShown = false
    this.props.state.preSelectedSiteLocation = null
  }

  @action
  private closeFormTypeSelectionForm = (): void => {
    this.isFormTypeSelectionFormShown = false
  }

  @action
  private dismissFormTypeSelectionForm = (): void => {
    this.closeFormTypeSelectionForm()
    this.props.state.preSelectedSiteLocation = null
  }

  private renderLinkedWorkflowPreview = (): JSX.Element => {
    const { linkedWorkflowToPreview, closeLinkedWorkflowPreview } = this.store

    return (
      <>
        <Dimmer
          shown={!!linkedWorkflowToPreview}
          onClick={closeLinkedWorkflowPreview}
          style={{ position: 'fixed', zIndex: 999 }}
        />
        {linkedWorkflowToPreview && (
          <div className="linked-workflow-preview">
            <WorkflowViewFormWrapper
              key={linkedWorkflowToPreview.id}
              workflowToShow={linkedWorkflowToPreview}
              onClose={closeLinkedWorkflowPreview}
              FileInputType={MobileFileInput}
              isMobileMode={true}
              openWorkflow={this.store.previewLinkedWorkflow}
            />
          </div>
        )}
      </>
    )
  }

  private closePermitViewForm = (): void => {
    this.store.isWorkflowViewFormShown = false
    if (window.location.search.includes(UrlParamKey.Form)) {
      // force URL params out of the history
      this.props.common.displayFormsView()
    }
  }

  private handledFormType: string = null

  private tryOpenFormTypeFromDeepLink(): void {
    const { deepLinkFormType, permitTypesStore, state } = this.props

    const result = resolveFormTypeDeepLink({
      deepLinkFormType,
      handledFormType: this.handledFormType,
      isDataReceived: permitTypesStore.isDataReceived,
      enabledTypes: permitTypesStore.enabledTypes,
      allTypes: permitTypesStore.actualTypes,
    })

    switch (result.action) {
      case 'skip':
        if (!deepLinkFormType) this.handledFormType = null
        return
      case 'wait':
        return
      case 'open':
        this.handledFormType = result.handledFormType
        removeFromSearchParam(UrlParamKey.FormType, this.props.common.history)
        this.store.isWorkflowViewFormShown = false
        this.isFormTypeSelectionFormShown = false
        state.preSelectedPermitTypeId = result.permitTypeId
        this.openPermitCreationForm()
        return
      case 'deny':
        this.handledFormType = result.handledFormType
        removeFromSearchParam(UrlParamKey.FormType, this.props.common.history)
        toast.error(Localization.translator.noPermissionToCreateWorkflowType)
        return
      case 'not_found':
        this.handledFormType = result.handledFormType
        removeFromSearchParam(UrlParamKey.FormType, this.props.common.history)
        return
    }
  }

  @action
  private openSpecificPermitCreationForm = (permitTypeId: string): void => {
    const { state } = this.props
    state.preSelectedPermitTypeId = permitTypeId
    this.openPermitCreationForm()
    this.closeFormTypeSelectionForm()
  }

  private get viewModes(): ViewMode[] {
    return [
      {
        mode: LogisticsPageViewMode.Calendar,
        Icon: CalendarIcon,
      },
      {
        mode: LogisticsPageViewMode.List,
        Icon: ListIcon,
      },
      {
        mode: LogisticsPageViewMode.Map,
        Icon: MapIcon,
        isDisabled: this.store.isMapViewDisabled,
      },
    ]
  }

  @action.bound
  private setDatePickerState(isShown: boolean) {
    this.isDatePickerShown = isShown
  }
}

const Logistics = inject(
  'eventsStore',
  'projectDateStore',
  'common',
)(
  observer((props: IProps) => {
    const { eventsStore, projectDateStore, common } = props
    const { appState } = eventsStore

    // Re-render on navigation so a ?form= deep link arriving while this view is
    // already mounted re-reads the URL and opens the form.
    const [, forceRenderOnNav] = useState(0)
    useEffect(() => {
      const onNav = () => forceRenderOnNav(n => n + 1)
      common.addNavigationListener(onNav)
      return () => common.removeNavigationListener(onNav)
    }, [common])

    const [datesFilter, setDatesFilter] = useState<{
      startDate?: number
      endDate?: number
    }>({
      startDate: projectDateStore.startOfDay(new Date()).getTime(),
      endDate: projectDateStore.endOfDay(new Date()).getTime(),
    })

    const { workflows, workflowsLoading } = useWorkflows(
      appState.activeProject.id,
      appState.user.id,
      datesFilter,
    )

    const searchParams = new URLSearchParams(location.search)
    const deepLinkFormId = searchParams.get(UrlParamKey.Form)
    const deepLinkFormType = searchParams.get(UrlParamKey.FormType)

    const { workflows: deepLinkedForms } = useWorkflowsByIds(
      appState.activeProject.id,
      deepLinkFormId ? [deepLinkFormId] : [],
    )

    return (
      <LogisticsComponent
        {...props}
        workflows={workflows}
        workflowsLoading={workflowsLoading}
        onDatesFilterChange={setDatesFilter}
        deepLinkedForm={deepLinkedForms?.[0]}
        deepLinkFormType={deepLinkFormType}
      />
    )
  }),
)

export default Logistics
